import React, { useState, useEffect } from 'react';
import { Search, User, Phone, Mail, Calendar, Eye, Plus, Key, ShieldCheck, CheckCircle2, UserPlus, Lock } from 'lucide-react';
import { api } from '../../api/client';
import { showToast } from '../../components/common/Toast';
import Modal from '../../components/common/Modal';
import PasswordInput from '../../components/common/PasswordInput';
import { getStatusBadge, formatUzPhone } from '../../utils/helpers';

export default function AdminClientsPage() {
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [selectedClient, setSelectedClient] = useState(null);
  const [clientHistory, setClientHistory] = useState([]);
  const [isHistoryModalOpen, setIsHistoryModalOpen] = useState(false);

  // New Client Modal
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [submittingAdd, setSubmittingAdd] = useState(false);
  const [newClientForm, setNewClientForm] = useState({
    fullName: '',
    phone: '+998 ()',
    username: '',
    password: '',
    email: '',
    region: 'Jizzax viloyati',
    district: 'Jizzax shahar',
    isFree: true,
  });

  // Account / Password Modal
  const [isAccountModalOpen, setIsAccountModalOpen] = useState(false);
  const [submittingAccount, setSubmittingAccount] = useState(false);
  const [accountClient, setAccountClient] = useState(null);
  const [accountForm, setAccountForm] = useState({
    username: '',
    password: '',
    isFree: true,
  });

  const loadClients = () => {
    setLoading(true);
    let url = '/clients?limit=50';
    if (search.trim()) url += `&search=${encodeURIComponent(search.trim())}`;

    api.get(url)
      .then((res) => {
        if (res?.success) setClients(res.clients || []);
      })
      .catch((err) => showToast(err.message, 'error'))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    const timer = setTimeout(() => {
      loadClients();
    }, 300);
    return () => clearTimeout(timer);
  }, [search]);

  const handleSearch = (e) => {
    e.preventDefault();
    loadClients();
  };

  const handleViewHistory = async (client) => {
    setSelectedClient(client);
    setIsHistoryModalOpen(true);
    try {
      const res = await api.get(`/clients/${client.id}`);
      if (res?.success) {
        setClientHistory(res.appointments || []);
      }
    } catch (err) {
      showToast(err.message, 'error');
    }
  };

  const handleOpenAddModal = () => {
    setNewClientForm({
      fullName: '',
      phone: '+998 ()',
      username: '',
      password: '',
      email: '',
      region: 'Jizzax viloyati',
      district: 'Jizzax shahar',
      isFree: true,
    });
    setIsAddModalOpen(true);
  };

  const handleAddClientSubmit = async (e) => {
    e.preventDefault();
    if (!newClientForm.fullName.trim()) {
      showToast('Mijoz ism-sharifini kiriting', 'error');
      return;
    }
    const cleanDigits = newClientForm.phone.replace(/\D/g, '');
    if (cleanDigits.length < 9) {
      showToast('To‘g‘ri telefon raqam kiriting: +998 (90) 123-45-67', 'error');
      return;
    }

    setSubmittingAdd(true);
    try {
      const cleanUsername = (newClientForm.username || `client_${cleanDigits.slice(-9)}`).trim().toLowerCase().replace(/^@+/, '');
      const res = await api.post('/clients', {
        fullName: newClientForm.fullName.trim(),
        phone: newClientForm.phone.trim(),
        username: cleanUsername,
        password: newClientForm.password.trim() || undefined,
        email: newClientForm.email?.trim() || undefined,
        region: newClientForm.region?.trim(),
        district: newClientForm.district?.trim(),
        isFree: newClientForm.isFree,
      });

      if (res?.success) {
        showToast(res.message || 'Mijoz muvaffaqiyatli qo‘shildi!', 'success');
        setIsAddModalOpen(false);
        loadClients();
      }
    } catch (err) {
      showToast(err.message || 'Mijoz qo‘shishda xatolik', 'error');
    } finally {
      setSubmittingAdd(false);
    }
  };

  const handleOpenAccountModal = (client) => {
    setAccountClient(client);
    setAccountForm({
      username: client.username || client.phone?.replace(/\D/g, '').slice(-9) ? `client_${client.phone.replace(/\D/g, '').slice(-9)}` : '',
      password: '',
      isFree: true,
    });
    setIsAccountModalOpen(true);
  };

  const handleAccountSubmit = async (e) => {
    e.preventDefault();
    if (!accountClient) return;

    setSubmittingAccount(true);
    try {
      const cleanUsername = accountForm.username.trim().toLowerCase().replace(/^@+/, '');
      const res = await api.post(`/clients/${accountClient.id}/account`, {
        username: cleanUsername,
        password: accountForm.password.trim() || undefined,
        isFree: accountForm.isFree,
      });

      if (res?.success) {
        showToast(res.message || 'Kirish paroli va ruxsat saqlandi!', 'success');
        setIsAccountModalOpen(false);
        loadClients();
      }
    } catch (err) {
      showToast(err.message || 'Akkauntni saqlashda xatolik', 'error');
    } finally {
      setSubmittingAccount(false);
    }
  };

  return (
    <div className="space-y-6 animate-admin-fade-in">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 sm:gap-4">
        <div>
          <h2 className="font-serif text-base sm:text-lg font-bold text-slate-900">
            Mijozlar Bazasi ({clients.length})
          </h2>
          <p className="text-xs text-slate-500">
            Advokatlar xizmatlariga murojaat qilgan va saytdan foydalanuvchi mijozlar
          </p>
        </div>

        <div className="flex items-center gap-2 w-full sm:w-auto">
          <button
            onClick={handleOpenAddModal}
            className="inline-flex items-center px-3.5 py-1.5 bg-[#c5a880] text-[#091322] text-xs font-bold rounded hover:bg-[#d4b993] transition-colors cursor-pointer shrink-0 shadow-2xs"
          >
            <UserPlus className="w-3.5 h-3.5 mr-1.5" />
            + Yangi mijoz
          </button>

          <form onSubmit={handleSearch} className="flex items-center gap-2 flex-1 sm:w-auto">
            <div className="relative flex-1 sm:w-56">
              <input
                type="text"
                placeholder="Ism, telefon yoki login..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="w-full pl-3 pr-7 py-1.5 border border-slate-300 rounded text-xs bg-white focus:outline-none focus:border-[#c5a880]"
              />
              {search && (
                <button
                  type="button"
                  onClick={() => setSearch('')}
                  className="absolute right-2 top-1.5 text-slate-400 hover:text-slate-600 text-xs font-bold"
                >
                  ✕
                </button>
              )}
            </div>
            <button
              type="submit"
              className="px-3.5 py-1.5 bg-[#091322] text-[#c5a880] text-xs font-semibold rounded hover:bg-[#11223b] transition-colors cursor-pointer shrink-0"
            >
              Qidirish
            </button>
          </form>
        </div>
      </div>

      <div className="bg-white rounded-lg border border-slate-200 shadow-2xs overflow-hidden">
        <div className="overflow-x-auto min-w-full">
          <table className="w-full text-left text-xs min-w-[700px]">
            <thead className="bg-slate-50 text-slate-500 border-b border-slate-200 uppercase tracking-wider text-[10px]">
              <tr>
                <th className="py-3 px-4 whitespace-nowrap">Mijoz Ismi & Login</th>
                <th className="py-3 px-4 whitespace-nowrap">Telefon</th>
                <th className="py-3 px-4 whitespace-nowrap">Hudud / Manzil</th>
                <th className="py-3 px-4 whitespace-nowrap">Jami Qabullar</th>
                <th className="py-3 px-4 whitespace-nowrap">Ro‘yxatdan o‘tgan</th>
                <th className="py-3 px-4 text-right whitespace-nowrap">Amallar</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {loading ? (
                <tr>
                  <td colSpan={6} className="text-center py-12 text-slate-400">Yuklanmoqda...</td>
                </tr>
              ) : clients.length === 0 ? (
                <tr>
                  <td colSpan={6} className="text-center py-12 text-slate-400">Mijozlar topilmadi</td>
                </tr>
              ) : (
                clients.map((c) => (
                  <tr key={c.id} className="hover:bg-slate-50 transition-colors">
                    <td className="py-3 px-4 whitespace-nowrap">
                      <div className="font-semibold text-slate-900">{c.fullName}</div>
                      <div className="text-[11px] font-mono text-slate-500 flex items-center gap-1 mt-0.5">
                        {c.username ? (
                          <span className="text-emerald-700 font-bold bg-emerald-50 px-1.5 py-0.2 rounded border border-emerald-200">
                            @{c.username}
                          </span>
                        ) : (
                          <span className="text-slate-400 italic">Login o‘rnatilmagan</span>
                        )}
                      </div>
                    </td>
                    <td className="py-3 px-4 font-mono text-slate-700 whitespace-nowrap tabular-nums">
                      {c.phone ? formatUzPhone(c.phone) : '—'}
                    </td>
                    <td className="py-3 px-4 text-slate-600 whitespace-nowrap">
                      {c.region ? `${c.region}${c.district ? `, ${c.district}` : ''}` : 'Jizzax viloyati'}
                    </td>
                    <td className="py-3 px-4 font-mono font-bold text-slate-800 whitespace-nowrap tabular-nums">
                      {c.totalAppointments || 0} ta
                    </td>
                    <td className="py-3 px-4 text-slate-500 whitespace-nowrap font-mono">
                      {new Date(c.createdAt).toLocaleDateString()}
                    </td>
                    <td className="py-3 px-4 text-right whitespace-nowrap">
                      <div className="inline-flex items-center gap-1.5 justify-end">
                        <button
                          onClick={() => handleOpenAccountModal(c)}
                          className="inline-flex items-center px-2 py-1 text-[11px] font-semibold text-[#091322] bg-[#c5a880]/20 hover:bg-[#c5a880]/35 border border-[#c5a880]/50 rounded transition-colors cursor-pointer"
                          title="Bosh sahifadan kirish uchun login va parol o‘rnatish"
                        >
                          <Key className="w-3.5 h-3.5 mr-1 text-[#091322]" />
                          Kirish paroli
                        </button>
                        <button
                          onClick={() => handleViewHistory(c)}
                          className="inline-flex items-center px-2 py-1 text-[11px] font-semibold text-slate-700 bg-slate-100 hover:bg-slate-200 rounded transition-colors cursor-pointer"
                        >
                          <Eye className="w-3.5 h-3.5 mr-1" />
                          Tarix
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Add Client Modal */}
      <Modal
        isOpen={isAddModalOpen}
        onClose={() => setIsAddModalOpen(false)}
        title="Yangi mijoz qo‘shish"
        maxWidth="max-w-lg"
      >
        <form onSubmit={handleAddClientSubmit} className="space-y-3.5 text-xs">
          <div className="bg-amber-50/80 border border-amber-200 text-amber-900 rounded p-2.5 text-[11px] leading-relaxed flex items-start gap-2">
            <ShieldCheck className="w-4 h-4 text-amber-700 shrink-0 mt-0.5" />
            <div>
              <strong>Cheksiz bepul ruxsat:</strong> Admin panelda qo‘shilgan mijoz bosh sahifadan o‘z logini va paroli orqali darhol bepul kura oladi va advokat bilan bog‘lana oladi.
            </div>
          </div>

          <div>
            <label className="block font-semibold text-slate-700 uppercase mb-1">To‘liq ism-sharif *</label>
            <input
              type="text"
              value={newClientForm.fullName}
              onChange={(e) => setNewClientForm({ ...newClientForm, fullName: e.target.value })}
              placeholder="Masalan: Rustamov Sanjarbek"
              className="w-full px-3 py-2 border rounded focus:border-[#c5a880] text-xs"
              required
            />
          </div>

          <div>
            <label className="block font-semibold text-slate-700 uppercase mb-1">Telefon raqami *</label>
            <input
              type="tel"
              value={newClientForm.phone}
              onFocus={() => {
                if (!newClientForm.phone || newClientForm.phone === '+998 ()') {
                  setNewClientForm((p) => ({ ...p, phone: '+998 ()' }));
                }
              }}
              onChange={(e) => setNewClientForm({ ...newClientForm, phone: formatUzPhone(e.target.value, newClientForm.phone) })}
              placeholder="+998 (90) 123-45-67"
              className="w-full px-3 py-2 border rounded font-mono focus:border-[#c5a880] text-xs"
              required
            />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="block font-semibold text-slate-700 uppercase mb-1">Login (Username)</label>
              <input
                type="text"
                value={newClientForm.username}
                onChange={(e) => setNewClientForm({ ...newClientForm, username: e.target.value.toLowerCase().trim() })}
                placeholder="Masalan: sanjar_rustamov"
                className="w-full px-3 py-2 border rounded font-mono focus:border-[#c5a880] text-xs"
              />
              <span className="text-[10px] text-slate-400">Bo‘sh qoldirilsa, avtomatik yaratiladi</span>
            </div>

            <div>
              <label className="block font-semibold text-slate-700 uppercase mb-1">Kirish paroli</label>
              <PasswordInput
                value={newClientForm.password}
                onChange={(e) => setNewClientForm({ ...newClientForm, password: e.target.value })}
                minLength={6}
                placeholder="Kamida 6 ta belgi..."
                className="w-full px-3 py-2 border rounded font-mono focus:border-[#c5a880] text-xs"
              />
              <span className="text-[10px] text-slate-400">Bo‘sh qoldirilsa telefon 6 ta raqami bo‘ladi</span>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="block font-semibold text-slate-700 uppercase mb-1">Viloyat</label>
              <input
                type="text"
                value={newClientForm.region}
                onChange={(e) => setNewClientForm({ ...newClientForm, region: e.target.value })}
                className="w-full px-3 py-2 border rounded focus:border-[#c5a880] text-xs"
              />
            </div>
            <div>
              <label className="block font-semibold text-slate-700 uppercase mb-1">Shahar / Tuman</label>
              <input
                type="text"
                value={newClientForm.district}
                onChange={(e) => setNewClientForm({ ...newClientForm, district: e.target.value })}
                className="w-full px-3 py-2 border rounded focus:border-[#c5a880] text-xs"
              />
            </div>
          </div>

          <label className="flex items-center gap-2 bg-slate-50 p-2.5 rounded border border-slate-200 cursor-pointer">
            <input
              type="checkbox"
              checked={newClientForm.isFree}
              onChange={(e) => setNewClientForm({ ...newClientForm, isFree: e.target.checked })}
              className="w-4 h-4 text-[#091322] rounded focus:ring-0"
            />
            <span className="font-semibold text-slate-800 text-xs">
              Mijozga cheksiz bepul ruxsat berish (to‘lovsiz sayt xizmatlaridan foydalanadi)
            </span>
          </label>

          <div className="pt-3 border-t flex justify-end gap-2">
            <button
              type="button"
              onClick={() => setIsAddModalOpen(false)}
              className="px-4 py-2 border rounded text-slate-700 hover:bg-slate-50 cursor-pointer"
            >
              Bekor qilish
            </button>
            <button
              type="submit"
              disabled={submittingAdd}
              className="px-5 py-2 bg-[#091322] text-[#c5a880] font-semibold rounded hover:bg-[#15243d] cursor-pointer disabled:opacity-50"
            >
              {submittingAdd ? 'Qo‘shilmoqda...' : 'Mijozni qo‘shish'}
            </button>
          </div>
        </form>
      </Modal>

      {/* Account / Password Modal */}
      {accountClient && (
        <Modal
          isOpen={isAccountModalOpen}
          onClose={() => setIsAccountModalOpen(false)}
          title={`Kirish hisobi: ${accountClient.fullName}`}
          maxWidth="max-w-md"
        >
          <form onSubmit={handleAccountSubmit} className="space-y-3.5 text-xs">
            <div className="bg-slate-50 p-3 rounded border text-xs">
              <div className="font-bold text-slate-900">{accountClient.fullName}</div>
              <div className="font-mono text-slate-500">{accountClient.phone ? formatUzPhone(accountClient.phone) : '—'}</div>
            </div>

            <div>
              <label className="block font-semibold text-slate-700 uppercase mb-1">Login (Username) *</label>
              <input
                type="text"
                value={accountForm.username}
                onChange={(e) => setAccountForm({ ...accountForm, username: e.target.value.toLowerCase().trim() })}
                placeholder="Masalan: mijoz_ali"
                className="w-full px-3 py-2 border rounded font-mono focus:border-[#c5a880] text-xs"
                required
              />
            </div>

            <div>
              <label className="block font-semibold text-slate-700 uppercase mb-1">Yangi Kirish Paroli *</label>
              <PasswordInput
                value={accountForm.password}
                onChange={(e) => setAccountForm({ ...accountForm, password: e.target.value })}
                minLength={6}
                placeholder="Kamida 6 ta belgi kiriting..."
                className="w-full px-3 py-2 border rounded font-mono focus:border-[#c5a880] text-xs"
                required
              />
            </div>

            <label className="flex items-center gap-2 bg-emerald-50 p-2.5 rounded border border-emerald-200 cursor-pointer">
              <input
                type="checkbox"
                checked={accountForm.isFree}
                onChange={(e) => setAccountForm({ ...accountForm, isFree: e.target.checked })}
                className="w-4 h-4 text-emerald-600 rounded focus:ring-0"
              />
              <span className="font-semibold text-emerald-900 text-xs">
                Cheksiz bepul ruxsatni faollashtirish (saytdan erkin foydalanadi)
              </span>
            </label>

            <div className="pt-3 border-t flex justify-end gap-2">
              <button
                type="button"
                onClick={() => setIsAccountModalOpen(false)}
                className="px-4 py-2 border rounded text-slate-700 hover:bg-slate-50 cursor-pointer"
              >
                Bekor qilish
              </button>
              <button
                type="submit"
                disabled={submittingAccount}
                className="px-5 py-2 bg-[#091322] text-[#c5a880] font-semibold rounded hover:bg-[#15243d] cursor-pointer disabled:opacity-50"
              >
                {submittingAccount ? 'Saqlanmoqda...' : 'Saqlash va ruxsat berish'}
              </button>
            </div>
          </form>
        </Modal>
      )}

      {/* History Modal */}
      {selectedClient && (
        <Modal
          isOpen={isHistoryModalOpen}
          onClose={() => setIsHistoryModalOpen(false)}
          title={`Mijoz Tarixi: ${selectedClient.fullName}`}
          maxWidth="max-w-2xl"
        >
          <div className="space-y-4 text-xs">
            <div className="bg-slate-50 p-3 rounded border flex justify-between">
              <div>
                <span className="font-semibold text-slate-900 block">{selectedClient.fullName}</span>
                <span className="text-slate-500 font-mono">{selectedClient.phone ? formatUzPhone(selectedClient.phone) : '—'}</span>
              </div>
              <div className="text-right">
                <span className="text-slate-500 block">Jami arizalar soni:</span>
                <span className="font-bold text-slate-900">{clientHistory.length} ta</span>
              </div>
            </div>

            <h4 className="font-bold uppercase tracking-wider text-slate-700 text-[11px]">
              Qabul Qilish Tarixi
            </h4>

            <div className="divide-y border rounded overflow-hidden">
              {clientHistory.length === 0 ? (
                <div className="p-4 text-center text-slate-400">Tarix mavjud emas</div>
              ) : (
                clientHistory.map((apt) => (
                  <div key={apt.id} className="p-3 flex items-center justify-between hover:bg-slate-50">
                    <div>
                      <span className="font-mono font-bold text-slate-900 mr-2">{apt.bookingReference}</span>
                      <span className="text-slate-600">{apt.service?.titleUz}</span>
                      <div className="text-[11px] text-slate-400 mt-0.5">
                        Advokat: {apt.lawyer?.fullName} | Sana: {apt.appointmentDate} soat {apt.startTime}
                      </div>
                    </div>
                    <div>
                      <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold border ${getStatusBadge(apt.status).classes}`}>
                        {getStatusBadge(apt.status).label}
                      </span>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}
